import React, { useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

import {
  FilterSelectOption,
  formatSingleSelectLabel,
  useFilterPanelFloatingMenu,
} from './filterDropdownShared';

type FilterDropdownSelectProps = {
  id: string;
  value: string;
  onChange: (next: string) => void;
  options: FilterSelectOption[];
  /** Rótulo quando value está vazio (ex.: Todos / Todas). */
  placeholderLabel?: string;
  /** Inclui opção vazia no topo; padrão true se placeholderLabel for informado. */
  showEmptyOption?: boolean;
  disabled?: boolean;
};

const FilterDropdownSelect: React.FC<FilterDropdownSelectProps> = ({
  id,
  value,
  onChange,
  options,
  placeholderLabel = 'Todos',
  showEmptyOption = true,
  disabled = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const listboxId = `${id}-listbox`;

  const includeEmpty = showEmptyOption && placeholderLabel !== '';
  const rowCount = options.length + (includeEmpty ? 1 : 0);

  const menuLayout = useFilterPanelFloatingMenu(open, rowCount, { wrapRef, triggerRef, menuRef }, () =>
    setOpen(false),
  );

  const displayLabel = useMemo(
    () => formatSingleSelectLabel(value, options, placeholderLabel),
    [options, placeholderLabel, value],
  );

  const handleOptionMouseDown = (event: React.MouseEvent, nextValue: string) => {
    event.preventDefault();
    if (disabled) return;
    onChange(nextValue);
    setOpen(false);
  };

  const menu =
    open && menuLayout ? (
      <ul
        ref={menuRef}
        id={listboxId}
        role="listbox"
        className="filter-panel__select-menu filter-panel__select-menu--floating"
        style={{
          top: menuLayout.top,
          left: menuLayout.left,
          width: menuLayout.width,
          height: menuLayout.height,
        }}
      >
        {includeEmpty ? (
          <li
            role="option"
            aria-selected={!value}
            className={`filter-panel__select-option${!value ? ' filter-panel__select-option--selected' : ''}`}
            onMouseDown={(event) => handleOptionMouseDown(event, '')}
          >
            {placeholderLabel}
          </li>
        ) : null}
        {options.map((option) => {
          const isSelected = value === option.value;
          return (
            <li
              key={option.value}
              role="option"
              aria-selected={isSelected}
              className={`filter-panel__select-option${isSelected ? ' filter-panel__select-option--selected' : ''}`}
              onMouseDown={(event) => handleOptionMouseDown(event, option.value)}
            >
              {option.label}
            </li>
          );
        })}
      </ul>
    ) : null;

  return (
    <div
      className={`filter-panel__select-wrap${open ? ' filter-panel__select-wrap--open' : ''}`}
      ref={wrapRef}
    >
      <button
        type="button"
        id={id}
        ref={triggerRef}
        role="combobox"
        className="filter-panel__control filter-panel__select filter-panel__select-trigger form-select form-select-sm"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="filter-panel__select-trigger-label text-truncate">{displayLabel}</span>
      </button>
      {menu ? createPortal(menu, document.body) : null}
    </div>
  );
};

export default FilterDropdownSelect;
